import { useRecapStore } from '../store/useRecapStore'
import { useDatabase } from '../composables/database'
import { TaskModel } from './TaskModel'
import { storeToRefs } from 'pinia'

type RecapDefinitionsModelReturn = {
  getAllRecapDefinitions: () => Promise<DatabaseRecapDefinition[]>
  getRecapDefinitionForTask: (task_id: string) => Promise<DatabaseRecapDefinition | undefined>
  writeRecapDefinitions: (responses: any) => Promise<boolean>
  removeStaleRecapDefinitions: () => Promise<void>
  clearRecapDefinitions: () => Promise<void>
  getRecapDefinition: (recap_id: string) => Promise<DatabaseRecapDefinition[] | void | null>
  /**Returns [{recap_id: int, hash: string}] */
  getRecapDefinitionHashes: () => Promise<any[]>
}

export type DatabaseRecapDefinition = {
  recap_id: string
  recap_definition: string
  task_ids: string
}

/**
 * Interacts with the recap definitions table (mobile only)
 * @returns {RecapDefinitionsModelReturn}
 */
export const RecapDefinitionsModel = (): RecapDefinitionsModelReturn => {
  const { run, query } = useDatabase()
  const recapStore = storeToRefs(useRecapStore())

  async function getAllRecapDefinitions(): Promise<DatabaseRecapDefinition[]> {
    const sql = 'SELECT * FROM recap_definitions'
    const definitions =
      ((await query(sql).catch((error) =>
        console.log(`Error fetching recap response: ${error}`)
      )) as unknown as DatabaseRecapDefinition[]) || []

    return definitions
  }

  const getRecapDefinition = async (
    recap_id: string
  ): Promise<DatabaseRecapDefinition[] | void | null> => {
    const sql = `
    SELECT recap_definition from recap_definitions
    WHERE recap_id = ${recap_id}
    LIMIT 1;
    `

    return await query(sql)
  }

  async function getRecapDefinitionForTask(
    task_id: string
  ): Promise<DatabaseRecapDefinition | undefined> {
    const possibleDefinitions = await getAllRecapDefinitions()
    if (possibleDefinitions && possibleDefinitions.length) {
      return possibleDefinitions.find((def) => {
        const taskIds = def.task_ids.split(',')
        if (taskIds.includes(task_id)) {
          return JSON.parse(def.recap_definition)
        }
      })
    }
    return undefined
  }

  async function writeRecapDefinitions(recaps: any): Promise<boolean> {
    const failedInserts: any[] = []
    const recapKeys = Object.keys(recaps.recaps || {})
    for (const key of recapKeys) {
      const recap = recaps.recaps[key] as any
      const updateSql = `
        INSERT OR REPLACE INTO recap_definitions ('recap_id', 'recap_definition', 'task_ids', 'hash')
        VALUES (?, ?, '', ?)
      `
      await run(updateSql, [
        recap.recap_definition.id,
        JSON.stringify(recap.recap_definition),
        recap.hash,
      ]).catch((error) => {
        console.log(`Error inserting or updating recap definition: ${error}`)
        failedInserts.push(key)
      })
    }
    const taskMappingKeys = Object.keys(recaps.task_mappings || {})
    for (const key of taskMappingKeys) {
      const taskMappings = recaps.task_mappings[key] as any
      const updateSql = `
        UPDATE recap_definitions
        SET task_ids = ?
        WHERE recap_id = ?
      `
      await run(updateSql, [taskMappings.join(','), key]).catch((error) => {
        console.log(`Error writing recap task mappings for recap ID ${key}: ${error}`)
        failedInserts.push(key)
      })
    }
    return !(failedInserts && failedInserts.length)
  }

  // Remove any images over 14 days old
  async function removeStaleRecapDefinitions() {
    const { getTasks } = TaskModel()
    const tasks = await getTasks()
    if (!tasks) {
      // There are no tasks, so we just want to blanket delete all recap definition data
      const sql = 'DELETE FROM recap_definitions'
      await run(sql, []).catch((error) =>
        console.error(`Error deleting stale recap definition data: ${error}`)
      )
    } else {
      // We have task data, go through each of the recaps and find what tasks they're associated with
      // If we find a recap definition that isn't associated to any of the tasks, delete it
      const taskIds = tasks.map((task) => (task as any).task_id)
      const recapsSql = 'SELECT * FROM recap_definitions'
      const recaps = await query(recapsSql).catch((error) =>
        console.error(`Error fetching all recap definitions: ${error}`)
      )
      if (recaps && recaps.length) {
        for (const recap of recaps) {
          const parsedTaskIds = recap.task_ids.split(',')
          if (!taskIds.some((id) => parsedTaskIds.includes(id))) {
            const sql = 'DELETE FROM recap_definitions WHERE recap_id = ?'
            await run(sql, [recap.recap_id]).catch((error) =>
              console.error(`Error deleting recap definition: ${error}`)
            )
          }
        }
      }
    }
  }

  async function clearRecapDefinitions() {
    const sql = 'DELETE FROM recap_definitions'
    await run(sql, [])
      .catch((error) => console.log(`Error deleting recap definitions: ${error}`))
      .then(() => {
        const keys = Object.keys(recapStore.recaps.value)
        for (const key of keys) {
          delete recapStore.recaps.value[key]
        }
      })
  }

  async function getRecapDefinitionHashes() {
    const sql = 'SELECT recap_id, hash from recap_definitions'
    const hashes = await query(sql)
    if (hashes) {
      return hashes
    } else {
      return []
    }
  }

  return {
    getAllRecapDefinitions,
    getRecapDefinitionForTask,
    writeRecapDefinitions,
    removeStaleRecapDefinitions,
    clearRecapDefinitions,
    getRecapDefinition,
    getRecapDefinitionHashes,
  }
}
